/**
 * FarmMapView.jsx - Carte interactive géolocalisée de la ferme
 *
 * Fonctionnalités :
 *   - Visualisation cartographique avec Leaflet
 *   - Sélection de fonds de carte (OpenStreetMap, satellite ESRI)
 *   - Affichage des capteurs avec dernières mesures
 *   - Délimitation des secteurs/serres
 *   - Mode édition pour placer capteurs et dessiner secteurs
 *   - Support GPS pour placement et enregistrement périmètre
 *   - Import GeoJSON/KML
 *   - Mode plein écran
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMapEvents, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Snackbar,
  IconButton,
  Button
} from '@mui/material';
import { Layers, Satellite, Fullscreen, FullscreenExit, Edit, Delete, Draw } from '@mui/icons-material';
import FarmMapService from './FarmMapService';
import MapEditToolbar from './MapEditToolbar';
import AddSensorDialog from './AddSensorDialog';
import AddSectorDialog from './AddSectorDialog';
import EditSensorDialog from './EditSensorDialog';
import EditSectorDialog from './EditSectorDialog';
import SensorMeasurementPopup from './SensorMeasurementPopup';

// Fix pour les icônes Leaflet avec Webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: iconShadow,
});

// Style CSS pour mettre en évidence le bouton "Draw a polygon"
const polygonButtonStyle = `
  /* Mettre en évidence le bouton Draw Polygon de Leaflet */
  .leaflet-draw-draw-polygon {
    background-color: #10b981 !important;
    border: 2px solid #059669 !important;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4) !important;
    transition: all 0.2s ease !important;
  }

  .leaflet-draw-draw-polygon:hover {
    background-color: #059669 !important;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.6) !important;
    transform: scale(1.05) !important;
  }

  /* Agrandir légèrement le bouton pour le rendre plus visible */
  .leaflet-draw-toolbar a {
    width: 32px !important;
    height: 32px !important;
    line-height: 32px !important;
  }
`;

// Créer une icône SVG personnalisée pour les capteurs
const createSensorSVG = (color) => `
  <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="shadow-${color}" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="1.5"/>
        <feOffset dx="0" dy="1" result="offsetblur"/>
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.4"/>
        </feComponentTransfer>
        <feMerge>
          <feMergeNode/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <!-- Cercle externe avec bordure -->
    <circle cx="16" cy="16" r="12" fill="${color}" stroke="white" stroke-width="2.5" filter="url(#shadow-${color})"/>
    <!-- Icône de capteur (ondes radio) -->
    <g transform="translate(16, 16)">
      <!-- Barres verticales représentant les ondes -->
      <rect x="-6" y="-5" width="2" height="10" fill="white" rx="1"/>
      <rect x="-2" y="-7" width="2" height="14" fill="white" rx="1"/>
      <rect x="2" y="-5" width="2" height="10" fill="white" rx="1"/>
      <!-- Point central -->
      <circle cx="0" cy="0" r="1.5" fill="white"/>
    </g>
  </svg>
`;

// Icônes personnalisées pour les capteurs
const sensorIcon = new L.DivIcon({
  html: createSensorSVG('#10b981'), // Vert pour capteurs avec GPS
  className: 'custom-sensor-icon',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const sensorWithoutGPSIcon = new L.DivIcon({
  html: createSensorSVG('#f59e0b'), // Orange pour capteurs sans GPS
  className: 'custom-sensor-icon',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

/**
 * Composant pour capturer la référence de la carte Leaflet
 */
function MapEventHandler({ onMapReady }) {
  const map = useMapEvents({});

  useEffect(() => {
    if (map && onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
}

/**
 * Composant pour centrer la carte sur un point
 */
function MapCenterController({ center, zoom }) {
  const map = useMapEvents({});

  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);

  return null;
}

/**
 * Composant pour gérer les clics sur la carte en mode édition
 */
function MapClickHandler({ enabled, onMapClick }) {
  useMapEvents({
    click(e) {
      if (enabled && onMapClick) {
        onMapClick(e.latlng);
      }
    }
  });

  return null;
}

/**
 * Composant pour invalider la taille de la carte (fullscreen)
 */
function InvalidateSize({ trigger }) {
  const map = useMapEvents({});

  useEffect(() => {
    if (!map) return;

    setTimeout(() => {
      try {
        map.invalidateSize();
      } catch (error) {
        console.warn('Erreur lors de l\'invalidation de la taille de la carte:', error);
      }
    }, 100);
  }, [trigger, map]);

  return null;
}

/**
 * Composant principal de la carte
 */
const FarmMapView = ({
  farmId,
  farmDetail,
  sectors = [],
  measurements = [],
  onSensorSelect,
  onSectorSelect
}) => {
  // ========================================================================
  // STATE MANAGEMENT
  // ========================================================================

  // État du composant - Données
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [farmLocation, setFarmLocation] = useState(null);
  const [sectorBoundaries, setSectorBoundaries] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState('osm');

  // État du composant - Édition
  const [editMode, setEditMode] = useState('view'); // 'view' ou 'edit'
  const [selectedSensorToPlace, setSelectedSensorToPlace] = useState(null);
  const [selectedSectorToDraw, setSelectedSectorToDraw] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);

  // État des dialogs
  const [showAddSensorDialog, setShowAddSensorDialog] = useState(false);
  const [showAddSectorDialog, setShowAddSectorDialog] = useState(false);
  const [showEditSensorDialog, setShowEditSensorDialog] = useState(false);
  const [sensorToEdit, setSensorToEdit] = useState(null);
  const [showEditSectorDialog, setShowEditSectorDialog] = useState(false);
  const [sectorToEdit, setSectorToEdit] = useState(null);
  const [initialDialogCoords, setInitialDialogCoords] = useState({ lat: null, lng: null });

  // État GPS
  const [isRecordingPerimeter, setIsRecordingPerimeter] = useState(false);
  const [perimeterPoints, setPerimeterPoints] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const watchIdRef = useRef(null);
  const polygonIdToSectorIdRef = useRef({});

  // État UI
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);

  // Références
  const mapRef = useRef(null);
  const editableFGRef = useRef(null);
  const containerRef = useRef(null);

  // Coordonnées par défaut (Ferme de Larache: 35°05'17.0"N 6°06'29.7"W)
  const DEFAULT_CENTER = [35.088056, -6.108250];
  const DEFAULT_ZOOM = 16;

  // ========================================================================
  // CHARGEMENT DES DONNÉES
  // ========================================================================

  /**
   * Charger les données de localisation de la ferme
   */
  useEffect(() => {
    const loadFarmLocation = async () => {
      if (!farmId) {
        setError('Aucune ferme sélectionnée');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        try {
          const location = await FarmMapService.getFarmLocation(farmId);
          setFarmLocation({
            latitude: parseFloat(location.latitude),
            longitude: parseFloat(location.longitude),
            altitude: location.altitude ? parseFloat(location.altitude) : null,
            name: farmDetail?.name || 'Ferme',
            locationSource: location.locationSource,
            notes: location.notes
          });
        } catch (apiError) {
          console.log('Localisation non trouvée, utilisation des coordonnées par défaut');
          setFarmLocation({
            latitude: DEFAULT_CENTER[0],
            longitude: DEFAULT_CENTER[1],
            name: farmDetail?.name || 'Ferme',
            locationSource: 'default'
          });
        }

      } catch (err) {
        console.error('Erreur lors du chargement de la localisation:', err);
        setError('Impossible de charger la localisation de la ferme');
      } finally {
        setLoading(false);
      }
    };

    loadFarmLocation();
  }, [farmId, farmDetail]);

  /**
   * Charger les capteurs avec leurs positions
   */
  useEffect(() => {
    const loadSensors = async () => {
      if (!farmId) return;

      try {
        const sensorsData = await FarmMapService.getSensorsWithLocation(farmId);
        setSensors(sensorsData);
        console.log(`✅ ${sensorsData.length} capteurs chargés`);
      } catch (err) {
        console.error('Erreur lors du chargement des capteurs:', err);
        setSensors([]);
      }
    };

    loadSensors();
  }, [farmId]);

  /**
   * Charger les délimitations des secteurs
   */
  useEffect(() => {
    const loadSectorBoundaries = async () => {
      if (!farmId) return;

      try {
        const boundaries = await FarmMapService.getSectorBoundaries(farmId);
        setSectorBoundaries(boundaries);
        console.log(`✅ ${boundaries.length} délimitations de secteurs chargées`);
      } catch (err) {
        console.error('Erreur lors du chargement des secteurs:', err);
        setSectorBoundaries([]);
      }
    };

    loadSectorBoundaries();
  }, [farmId]);

  /**
   * Injecter le style CSS pour mettre en évidence le bouton polygon
   */
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'polygon-button-highlight';
    styleElement.textContent = polygonButtonStyle;

    if (!document.getElementById('polygon-button-highlight')) {
      document.head.appendChild(styleElement);
    }

    return () => {
      const existingStyle = document.getElementById('polygon-button-highlight');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  /**
   * Callback pour capturer la référence de la carte Leaflet
   */
  const handleMapReady = useCallback((map) => {
    if (map && !mapRef.current) {
      mapRef.current = map;
      console.log('✅ Carte Leaflet initialisée et référence capturée');
    }
  }, []);

  // ========================================================================
  // CALCULS DÉRIVÉS
  // ========================================================================

  /**
   * Calculer TOUS les capteurs et secteurs (pour affichage complet)
   */
  const allSensors = useMemo(() => {
    console.log('🔍 allSensors - sensors count:', sensors.length);
    return sensors;
  }, [sensors]);

  // Afficher les secteurs tels qu'ils sont dans la BD (pas de fusion avec boundaries)
  const allSectors = useMemo(() => {
    const result = sectors || [];
    console.log('🔍 allSectors - sectors from props:', result.length);
    if (result.length > 0) {
      console.log('   Détails secteurs:', result.map(s => ({
        sector_id: s.sector_id || s.sectorId,
        name: s.name,
        compartment_name: s.compartment_name || s.compartmentName
      })));
    }
    console.log('✅ allSectors final:', result.length, 'secteurs');
    return result;
  }, [sectors]);

  /**
   * Calculer les capteurs sans GPS et secteurs sans boundary (pour statistiques)
   */
  const sensorsWithoutGPS = useMemo(() => {
    return sensors.filter(s => !s.gps_latitude || !s.gps_longitude);
  }, [sensors]);

  const sectorsWithoutBoundary = useMemo(() => {
    // Source de données: sectors si disponible, sinon sectorBoundaries
    const allSectorsForFiltering = sectors && sectors.length > 0 ? sectors : sectorBoundaries;

    // Filtrer pour trouver ceux sans délimitation
    return allSectorsForFiltering.filter(s => {
      const sectorId = s.sector_id || s.sectorId;
      const boundary = sectorBoundaries.find(b =>
        (b.sector_id || b.sectorId) === sectorId
      );
      return !boundary || (!boundary.boundary_polygon && !boundary.boundaryPolygon);
    });
  }, [sectors, sectorBoundaries]);

  /**
   * Calculer le centre de la carte
   */
  const mapCenter = useMemo(() => {
    if (userLocation && editMode === 'edit') {
      return [userLocation.latitude, userLocation.longitude];
    }
    if (farmLocation) {
      return [farmLocation.latitude, farmLocation.longitude];
    }
    return DEFAULT_CENTER;
  }, [farmLocation, userLocation, editMode]);

  // ========================================================================
  // HANDLERS - PLACEMENT DES CAPTEURS
  // ========================================================================

  /**
   * Gérer le clic sur la carte pour placer un capteur
   */
  const handleMapClick = useCallback(async (latlng) => {
    if (editMode !== 'edit' || !selectedSensorToPlace) return;

    try {
      const location = {
        farmId: farmId,
        latitude: latlng.lat,
        longitude: latlng.lng,
        locationSource: 'manual',
        locationDescription: 'Placé manuellement sur la carte'
      };

      setPendingChanges(prev => [...prev, {
        type: 'sensor',
        source_id: selectedSensorToPlace,
        location
      }]);

      setSensors(prev => prev.map(s =>
        s.source_id === selectedSensorToPlace
          ? { ...s, gps_latitude: latlng.lat, gps_longitude: latlng.lng }
          : s
      ));

      setSnackbarMessage(`Capteur ${selectedSensorToPlace} placé - Cliquez sur "Sauvegarder" pour enregistrer`);
      setSelectedSensorToPlace(null);

    } catch (err) {
      console.error('Erreur lors du placement du capteur:', err);
      setSnackbarMessage('Erreur lors du placement du capteur');
    }
  }, [editMode, selectedSensorToPlace, farmId]);

  /**
   * Utiliser la position GPS de l'utilisateur pour placer un capteur
   */
  const handleUseMyLocationForSensor = useCallback(() => {
    if (!selectedSensorToPlace) return;

    if (!navigator.geolocation) {
      setSnackbarMessage('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latlng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        handleMapClick(latlng);
        setSnackbarMessage(`Position GPS utilisée: ${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`);
      },
      (error) => {
        setSnackbarMessage(`Erreur GPS: ${error.message}`);
      }
    );
  }, [selectedSensorToPlace, handleMapClick]);

  // ========================================================================
  // HANDLERS - DESSIN DES SECTEURS
  // ========================================================================

  /**
   * Gérer la création d'un polygone pour un secteur
   */
  const handlePolygonCreated = useCallback((e) => {
    if (!selectedSectorToDraw) {
      setSnackbarMessage('Veuillez d\'abord sélectionner un secteur à délimiter');
      return;
    }

    const layer = e.layer;
    const latlngs = layer.getLatLngs()[0];

    // Convertir en format GeoJSON [lng, lat]
    const coordinates = latlngs.map(latlng => [latlng.lng, latlng.lat]);
    coordinates.push(coordinates[0]); // Fermer le polygone

    const boundary = {
      type: 'Polygon',
      coordinates: [coordinates]
    };

    const area = FarmMapService.calculatePolygonArea(coordinates);

    setPendingChanges(prev => [...prev, {
      type: 'sector',
      sector_id: selectedSectorToDraw,
      boundary: {
        coordinates: coordinates,
        area: area
      }
    }]);

    setSectorBoundaries(prev => {
      const existing = prev.find(s => s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw);
      if (existing) {
        return prev.map(s =>
          (s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw)
            ? { ...s, boundary_polygon: boundary, boundaryPolygon: boundary, area_square_meters: area, areaSquareMeters: area }
            : s
        );
      } else {
        const sector = sectors.find(s => s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw);
        return [...prev, {
          sector_id: selectedSectorToDraw,
          sectorId: selectedSectorToDraw,
          name: sector?.name,
          boundary_polygon: boundary,
          boundaryPolygon: boundary,
          area_square_meters: area,
          areaSquareMeters: area
        }];
      }
    });

    setSnackbarMessage(`Secteur délimité (${Math.round(area)} m²) - Cliquez sur "Sauvegarder" pour enregistrer`);
    setSelectedSectorToDraw(null);
    setIsDrawingPolygon(false); // Désactiver l'indicateur de dessin

    if (editableFGRef.current) {
      editableFGRef.current.removeLayer(layer);
    }
  }, [selectedSectorToDraw, sectors]);

  /**
   * Utiliser la position GPS pour recentrer la carte sur un secteur
   */
  const handleUseMyLocationForSector = useCallback(() => {
    if (!selectedSectorToDraw) return;

    if (!navigator.geolocation) {
      setSnackbarMessage('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setSnackbarMessage(`Carte recentrée sur votre position. Tracez maintenant le polygone.`);
      },
      (error) => {
        setSnackbarMessage(`Erreur GPS: ${error.message}`);
      }
    );
  }, [selectedSectorToDraw]);

  /**
   * Démarrer l'enregistrement du périmètre GPS
   */
  const handleStartPerimeter = useCallback(() => {
    if (!selectedSectorToDraw) return;

    if (!navigator.geolocation) {
      setSnackbarMessage('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    setIsRecordingPerimeter(true);
    setPerimeterPoints([]);
    setSnackbarMessage('Enregistrement du périmètre démarré. Marchez autour du secteur.');

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const point = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setPerimeterPoints(prev => [...prev, point]);
      },
      (error) => {
        setSnackbarMessage(`Erreur GPS: ${error.message}`);
        handleStopPerimeter();
      },
      { enableHighAccuracy: true, maximumAge: 0 }
    );
  }, [selectedSectorToDraw]);

  /**
   * Arrêter l'enregistrement du périmètre GPS
   */
  const handleStopPerimeter = useCallback(() => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setIsRecordingPerimeter(false);

    if (perimeterPoints.length < 3) {
      setSnackbarMessage('Pas assez de points pour créer un polygone (minimum 3)');
      setPerimeterPoints([]);
      return;
    }

    // Créer un polygone à partir des points GPS
    const coordinates = perimeterPoints.map(p => [p.lng, p.lat]);
    coordinates.push(coordinates[0]); // Fermer le polygone

    const boundary = {
      type: 'Polygon',
      coordinates: [coordinates]
    };

    const area = FarmMapService.calculatePolygonArea(coordinates);

    setPendingChanges(prev => [...prev, {
      type: 'sector',
      sector_id: selectedSectorToDraw,
      boundary: {
        coordinates: coordinates,
        area: area
      }
    }]);

    setSectorBoundaries(prev => {
      const existing = prev.find(s => s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw);
      if (existing) {
        return prev.map(s =>
          (s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw)
            ? { ...s, boundary_polygon: boundary, boundaryPolygon: boundary, area_square_meters: area, areaSquareMeters: area }
            : s
        );
      } else {
        const sector = sectors.find(s => s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw);
        return [...prev, {
          sector_id: selectedSectorToDraw,
          sectorId: selectedSectorToDraw,
          name: sector?.name,
          boundary_polygon: boundary,
          boundaryPolygon: boundary,
          area_square_meters: area,
          areaSquareMeters: area
        }];
      }
    });

    setSnackbarMessage(`Périmètre GPS enregistré: ${perimeterPoints.length} points, ${Math.round(area)} m²`);
    setPerimeterPoints([]);
    setSelectedSectorToDraw(null);
  }, [perimeterPoints, selectedSectorToDraw, sectors]);

  /**
   * Importer un fichier GeoJSON/KML
   */
  const handleImportBoundaryFile = useCallback((fileContent, filename) => {
    if (!selectedSectorToDraw) {
      setSnackbarMessage('Veuillez d\'abord sélectionner un secteur');
      return;
    }

    try {
      let boundary;

      if (filename.endsWith('.geojson') || filename.endsWith('.json')) {
        const geojson = JSON.parse(fileContent);

        if (geojson.type === 'FeatureCollection' && geojson.features && geojson.features.length > 0) {
          boundary = geojson.features[0].geometry;
        } else if (geojson.type === 'Feature') {
          boundary = geojson.geometry;
        } else if (geojson.type === 'Polygon') {
          boundary = geojson;
        } else {
          throw new Error('Format GeoJSON non reconnu');
        }
      } else if (filename.endsWith('.kml')) {
        setSnackbarMessage('Import KML non encore supporté. Utilisez GeoJSON.');
        return;
      }

      if (!boundary || boundary.type !== 'Polygon') {
        throw new Error('Le fichier doit contenir un polygone');
      }

      const area = FarmMapService.calculatePolygonArea(boundary.coordinates[0]);

      setPendingChanges(prev => [...prev, {
        type: 'sector',
        sector_id: selectedSectorToDraw,
        boundary: {
          coordinates: boundary.coordinates[0],
          area: area
        }
      }]);

      setSectorBoundaries(prev => {
        const existing = prev.find(s => s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw);
        if (existing) {
          return prev.map(s =>
            (s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw)
              ? { ...s, boundary_polygon: boundary, boundaryPolygon: boundary, area_square_meters: area, areaSquareMeters: area }
              : s
          );
        } else {
          const sector = sectors.find(s => s.sector_id === selectedSectorToDraw || s.sectorId === selectedSectorToDraw);
          return [...prev, {
            sector_id: selectedSectorToDraw,
            sectorId: selectedSectorToDraw,
            name: sector?.name,
            boundary_polygon: boundary,
            boundaryPolygon: boundary,
            area_square_meters: area,
            areaSquareMeters: area
          }];
        }
      });

      setSnackbarMessage(`Fichier importé: ${Math.round(area)} m²`);
      setSelectedSectorToDraw(null);

    } catch (err) {
      console.error('Erreur lors de l\'import:', err);
      setSnackbarMessage(`Erreur lors de l'import: ${err.message}`);
    }
  }, [selectedSectorToDraw, sectors]);

  // ========================================================================
  // HANDLERS - DIALOGS
  // ========================================================================

  /**
   * Ouvrir le dialog de création de capteur
   */
  const handleOpenAddSensor = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setInitialDialogCoords({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {
          setInitialDialogCoords({ lat: null, lng: null });
        }
      );
    } else {
      setInitialDialogCoords({ lat: null, lng: null });
    }
    setShowAddSensorDialog(true);
  }, []);

  /**
   * Callback quand un capteur est ajouté
   */
  const handleSensorAdded = useCallback(async (newSensor) => {
    setSnackbarMessage(`Capteur ${newSensor.source_id || newSensor.sourceId} créé avec succès`);
    setShowAddSensorDialog(false);

    // Recharger les capteurs
    try {
      const sensorsData = await FarmMapService.getSensorsWithLocation(farmId);
      setSensors(sensorsData);
    } catch (err) {
      console.error('Erreur lors du rechargement des capteurs:', err);
    }
  }, [farmId]);

  /**
   * Ouvrir le dialog d'édition de capteur
   */
  const handleOpenEditSensor = useCallback((sensor) => {
    setSensorToEdit(sensor);
    setShowEditSensorDialog(true);
  }, []);

  /**
   * Callback quand un capteur est modifié
   */
  const handleSensorUpdated = useCallback(async (updatedSensor) => {
    setSnackbarMessage(`Capteur ${updatedSensor.source_id || updatedSensor.sourceId} modifié avec succès`);
    setShowEditSensorDialog(false);
    setSensorToEdit(null);

    // Recharger les capteurs
    try {
      const sensorsData = await FarmMapService.getSensorsWithLocation(farmId);
      setSensors(sensorsData);
    } catch (err) {
      console.error('Erreur lors du rechargement des capteurs:', err);
    }
  }, [farmId]);

  /**
   * Ouvrir le dialog d'édition de secteur
   */
  const handleOpenEditSector = useCallback((sector) => {
    setSectorToEdit(sector);
    setShowEditSectorDialog(true);
  }, []);

  /**
   * Callback quand un secteur est modifié
   */
  const handleSectorUpdated = useCallback(async (updatedSector) => {
    setSnackbarMessage(`Secteur ${updatedSector.sector_id || updatedSector.sectorId} modifié avec succès`);
    setShowEditSectorDialog(false);
    setSectorToEdit(null);

    // Recharger les secteurs
    try {
      const boundaries = await FarmMapService.getSectorBoundaries(farmId);
      setSectorBoundaries(boundaries);
    } catch (err) {
      console.error('Erreur lors du rechargement des secteurs:', err);
    }
  }, [farmId]);

  /**
   * Préparer le mode dessin pour redessiner un secteur
   */
  const handleStartNewBoundary = useCallback((sector) => {
    const sectorId = sector.sector_id || sector.sectorId;

    // Passer en mode édition
    setEditMode('edit');

    // Pré-sélectionner le secteur à redessiner
    setSelectedSectorToDraw(sectorId);

    // Afficher le panneau d'instructions (mais ne pas démarrer le dessin automatiquement)
    setIsDrawingPolygon(true);
  }, []);

  /**
   * Annuler le dessin en cours
   */
  const handleCancelDrawing = useCallback(() => {
    setIsDrawingPolygon(false);
    setEditMode('view');
    setSelectedSectorToDraw(null);
    setSnackbarMessage('Dessin annulé');
  }, []);

  /**
   * Supprimer un secteur
   */
  const handleDeleteSector = useCallback(async (sector) => {
    const sectorId = sector.sector_id || sector.sectorId;
    const sectorName = sector.name || `Secteur ${sectorId}`;

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le secteur "${sectorName}" ?\n\nCette action est irréversible.`)) {
      return;
    }

    try {
      await FarmMapService.deleteSector(sectorId);
      setSnackbarMessage(`Secteur "${sectorName}" supprimé avec succès`);

      // Recharger les secteurs
      const boundaries = await FarmMapService.getSectorBoundaries(farmId);
      setSectorBoundaries(boundaries);
    } catch (err) {
      console.error('Erreur lors de la suppression du secteur:', err);
      setSnackbarMessage(`Erreur: ${err.message}`);
    }
  }, [farmId]);


  /**
   * Ouvrir le dialog de création de secteur
   */
  const handleOpenAddSector = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setInitialDialogCoords({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {
          setInitialDialogCoords({ lat: null, lng: null });
        }
      );
    } else {
      setInitialDialogCoords({ lat: null, lng: null });
    }
    setShowAddSectorDialog(true);
  }, []);

  /**
   * Callback quand un secteur est ajouté
   */
  const handleSectorAdded = useCallback(async (newSector) => {
    setSnackbarMessage(`Secteur ${newSector.name || newSector.sector_id || newSector.sectorId} créé avec succès`);
    setShowAddSectorDialog(false);

    // Recharger les secteurs via le parent (FarmRoadDashboard)
    // Note: Vous devrez peut-être ajouter un callback onRefresh dans les props
  }, []);

  // ========================================================================
  // HANDLERS - SAUVEGARDE
  // ========================================================================

  /**
   * Sauvegarder toutes les modifications
   */
  const handleSaveChanges = async () => {
    if (pendingChanges.length === 0) {
      setSnackbarMessage('Aucune modification à sauvegarder');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const change of pendingChanges) {
        try {
          if (change.type === 'sensor') {
            await FarmMapService.setSensorLocation(change.source_id, change.location);
            successCount++;
          } else if (change.type === 'sector') {
            await FarmMapService.setSectorBoundary(change.sector_id, change.boundary);
            successCount++;
          }
        } catch (err) {
          console.error(`Erreur lors de la sauvegarde de ${change.type} ${change.source_id || change.sector_id}:`, err);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setSnackbarMessage(`✅ ${successCount} modification(s) sauvegardée(s) avec succès`);
        setPendingChanges([]);
      } else {
        setSnackbarMessage(`⚠️ ${successCount} sauvegardées, ${errorCount} erreur(s)`);
      }

    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setSnackbarMessage('❌ Erreur lors de la sauvegarde');
    }
  };

  /**
   * Annuler les modifications
   */
  const handleCancelChanges = async () => {
    setPendingChanges([]);
    setSelectedSensorToPlace(null);
    setSelectedSectorToDraw(null);
    setEditMode('view');
    setSnackbarMessage('Modifications annulées');

    // Reload data without full page refresh
    try {
      const [sensorsData, boundaries] = await Promise.all([
        FarmMapService.getSensorsWithLocation(farmId),
        FarmMapService.getSectorBoundaries(farmId)
      ]);
      setSensors(sensorsData);
      setSectorBoundaries(boundaries);
    } catch (error) {
      console.error('Erreur lors du rechargement des données:', error);
    }
  };

  // ========================================================================
  // HANDLERS - PLEIN ÉCRAN
  // ========================================================================

  /**
   * Toggle plein écran
   */
  const toggleFullscreen = useCallback(() => {
    // Plein écran CSS (pas de requestFullscreen)
    setIsFullscreen(prev => !prev);
  }, []);

  useEffect(() => {
    // Verrouiller le scroll de la page en mode plein écran CSS
    const prevOverflow = document.body.style.overflow;
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = prevOverflow || '';
    }
    return () => {
      document.body.style.overflow = prevOverflow || '';
    };
  }, [isFullscreen]);

  // ========================================================================
  // RENDU
  // ========================================================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement de la carte...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Debug: Log avant le rendu
  console.log('📍 FarmMapView - Avant rendu MapEditToolbar:', {
    editMode,
    allSensors_count: allSensors.length,
    allSectors_count: allSectors.length
  });

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: isFullscreen ? '100vh' : 'calc(100vh - 250px)',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 1200 : 'auto',
        backgroundColor: isFullscreen ? '#000' : 'transparent'
      }}
    >
      {/* Barre d'outils d'édition */}
      <MapEditToolbar
        editMode={editMode}
        onEditModeChange={setEditMode}
        allSensors={allSensors}
        allSectors={allSectors}
        sensorsWithoutGPS={sensorsWithoutGPS}
        sectorsWithoutBoundary={sectorsWithoutBoundary}
        selectedSensorToPlace={selectedSensorToPlace}
        selectedSectorToDraw={selectedSectorToDraw}
        onSensorSelect={setSelectedSensorToPlace}
        onSectorSelect={setSelectedSectorToDraw}
        onSave={handleSaveChanges}
        onCancel={handleCancelChanges}
        hasUnsavedChanges={pendingChanges.length > 0}
        onUseMyLocationForSensor={handleUseMyLocationForSensor}
        onUseMyLocationForSector={handleUseMyLocationForSector}
        isRecordingPerimeter={isRecordingPerimeter}
        onStartPerimeter={handleStartPerimeter}
        onStopPerimeter={handleStopPerimeter}
        onImportBoundaryFile={handleImportBoundaryFile}
        onOpenAddSensor={handleOpenAddSensor}
        onOpenAddSector={handleOpenAddSector}
        isFullscreen={isFullscreen}
      />


      {/* Sélecteur de couche de fond */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: isFullscreen ? 'auto' : 10,
          bottom: isFullscreen ? 10 : 'auto',
          right: 10,
          zIndex: 1205,
          p: 1,
          display: 'flex',
          gap: 1
        }}
      >
        <ToggleButtonGroup
          value={selectedLayer}
          exclusive
          onChange={(e, newLayer) => newLayer && setSelectedLayer(newLayer)}
          size="small"
        >
          <ToggleButton value="osm" aria-label="carte standard">
            <Layers fontSize="small" />
            <Typography variant="caption" sx={{ ml: 0.5 }}>Carte</Typography>
          </ToggleButton>
          <ToggleButton value="satellite" aria-label="vue satellite">
            <Satellite fontSize="small" />
            <Typography variant="caption" sx={{ ml: 0.5 }}>Satellite</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <IconButton
          onClick={toggleFullscreen}
          size="small"
          sx={{ borderRadius: 1, border: '1px solid rgba(0,0,0,0.23)' }}
        >
          {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
      </Paper>

      {/* Informations de la ferme */}
      {isFullscreen && farmLocation && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 1000,
            p: 2,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            maxWidth: 300
          }}
        >
          <Typography variant="h6" gutterBottom>
            {farmLocation.name}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Secteurs: {sectors.length} | Capteurs: {sensors.filter(s => s.gps_latitude && s.gps_longitude).length}/{sensors.length}
          </Typography>
          {isRecordingPerimeter && (
            <Chip
              label={`GPS: ${perimeterPoints.length} points`}
              color="error"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Paper>
      )}

      {/* Panneau d'instructions pour le dessin de polygone */}
      {isDrawingPolygon && (
        <Paper
          elevation={6}
          sx={{
            position: 'absolute',
            top: isFullscreen ? 20 : 30,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            p: 2.5,
            minWidth: 400,
            maxWidth: 550,
            backgroundColor: '#fff',
            borderTop: '4px solid #10b981'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Draw sx={{ color: '#10b981', fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Redessiner le secteur
              </Typography>
            </Box>

            <Box sx={{ bgcolor: '#f0fdf4', p: 2, borderRadius: 1, border: '1px solid #86efac' }}>
              <Typography variant="body2" fontWeight="bold" color="text.primary" gutterBottom>
                📍 Instructions :
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 1 }}>
                <strong>1.</strong> Cliquez sur l'icône de polygone en haut à gauche <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#10b981', color: '#000000', px: 0.5, py: 0.25, borderRadius: 0.5, mx: 0.5, fontWeight: 'bold', fontSize: '1rem', border: '1px solid #059669' }}>⬟</Box><br />
                <strong>2.</strong> Dessinez le nouveau contour du secteur sur la carte<br />
                <strong>3.</strong> Cliquez sur le premier point pour fermer<br />
                <strong>4.</strong> Le nouveau polygone remplacera l'ancien
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleCancelDrawing}
                startIcon={<Delete />}
              >
                Annuler
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Carte Leaflet */}
      <MapContainer
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        {/* Capturer la référence de la carte Leaflet */}
        <MapEventHandler onMapReady={handleMapReady} />

        {/* Contrôleur de centrage */}
        <MapCenterController center={mapCenter} zoom={DEFAULT_ZOOM} />

        {/* Invalidate size pour fullscreen */}
        <InvalidateSize trigger={isFullscreen} />

        {/* Gestionnaire de clics pour placement de capteurs */}
        <MapClickHandler
          enabled={editMode === 'edit' && selectedSensorToPlace !== null}
          onMapClick={handleMapClick}
        />

        {/* Couche OpenStreetMap */}
        {selectedLayer === 'osm' && (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
        )}

        {/* Couche Satellite ESRI */}
        {selectedLayer === 'satellite' && (
          <TileLayer
            attribution='Tiles &copy; Esri'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
        )}

        {/* Groupe de fonctionnalités éditables */}
        <FeatureGroup ref={editableFGRef}>
          {editMode === 'edit' && (
            <EditControl
              position="topleft"
              onCreated={handlePolygonCreated}
              onEdited={(e) => {
                try {
                  e.layers.eachLayer((layer) => {
                    const latlngs = layer.getLatLngs?.()[0];
                    if (!latlngs || latlngs.length < 3) return;
                    const coords = latlngs.map(ll => [ll.lng, ll.lat]);
                    coords.push(coords[0]);
                    const area = FarmMapService.calculatePolygonArea(coords);
                    const sectorId = polygonIdToSectorIdRef.current?.[layer._leaflet_id];
                    if (!sectorId) return;
                    setPendingChanges(prev => [...prev, {
                      type: 'sector',
                      sector_id: sectorId,
                      boundary: { coordinates: coords, area }
                    }]);
                    setSectorBoundaries(prev => prev.map(s =>
                      (s.sector_id === sectorId || s.sectorId === sectorId)
                        ? { ...s, boundary_polygon: { type: 'Polygon', coordinates: [coords] }, area_square_meters: area, boundaryPolygon: { type: 'Polygon', coordinates: [coords] }, areaSquareMeters: area }
                        : s
                    ));
                  });
                  setSnackbarMessage('Délimitation mise à jour — Sauvegardez pour enregistrer');
                } catch (err) {
                  console.warn('Edition error:', err);
                }
              }}
              draw={selectedSectorToDraw ? {
                rectangle: false,
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
                polygon: { allowIntersection: false, showArea: true, metric: true }
              } : {
                rectangle: false,
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
                polygon: false
              }}
              edit={{
                edit: {
                  selectedPathOptions: {
                    maintainColor: false,
                    opacity: 0.6,
                    dashArray: '10, 10',
                    fillOpacity: 0.3,
                    weight: 3
                  }
                },
                remove: false
              }}
            />
          )}

          {/* Polygones des secteurs (éditables) */}
          {sectorBoundaries
            .filter((sector, idx, self) => {
              const sectorId = sector.sector_id || sector.sectorId;
              return sectorId && idx === self.findIndex(s => (s.sector_id || s.sectorId) === sectorId);
            })
            .map((sector, idx) => {
              const polygon = sector.boundary_polygon || sector.boundaryPolygon;
              if (!polygon || !polygon.coordinates) {
                console.log(`⚠️ Secteur ${sector.sector_id || sector.sectorId} sans polygone`);
                return null;
              }
              const positions = polygon.coordinates[0].map(coord => [coord[1], coord[0]]);
              const colors = ['#3388ff', '#ff3388', '#33ff88', '#ff8833', '#8833ff', '#33ffff'];
              const color = colors[idx % colors.length];
              const sectorId = sector.sector_id || sector.sectorId;
              const sectorName = sector.name || `Secteur ${sectorId}`;
              const area = sector.area_square_meters || sector.areaSquareMeters;
              const sensorCount = sector.sensor_count || sector.sectorCount || 0;

              // Clé unique incluant les coordonnées pour forcer le re-render si la délimitation change
              const polygonKey = `${sectorId}-${JSON.stringify(positions[0])}-${positions.length}`;

              console.log(`🗺️ Rendu polygone ${sectorId}:`, {
                key: polygonKey.substring(0, 50) + '...',
                positions: positions.length,
                area,
                color,
                firstPoint: positions[0],
                allPoints: positions
              });

              return (
                <Polygon
                  key={polygonKey}
                  positions={positions}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.2, weight: 2 }}
                  eventHandlers={{
                    add: (e) => {
                      if (sectorId) {
                        polygonIdToSectorIdRef.current[e.target._leaflet_id] = String(sectorId);
                        // Rendre le polygone éditable en mode édition
                        if (editMode === 'edit' && editableFGRef.current) {
                          e.target.options.interactive = true;
                          e.target.options.bubblingMouseEvents = false;
                        }
                      }
                    },
                    click: () => {
                      if (onSectorSelect && editMode === 'view') {
                        onSectorSelect(sectorId);
                      }
                    }
                  }}
                >
                  {editMode === 'view' && (
                    <Popup>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {sectorName}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          ID: {sectorId}
                        </Typography>

                        {area && typeof area === 'number' && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            📏 Surface: {area.toFixed(2)} m²
                          </Typography>
                        )}

                        <Typography variant="body2" sx={{ mb: 2 }}>
                          📡 Capteurs: {sensorCount}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Draw />}
                            onClick={() => {
                              handleStartNewBoundary(sector);
                              // Fermer le popup
                              document.querySelector('.leaflet-popup-close-button')?.click();
                            }}
                            fullWidth
                          >
                            Nouvelle délimitation
                          </Button>

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => handleOpenEditSector(sector)}
                              fullWidth
                            >
                              Éditer
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteSector(sector)}
                              fullWidth
                            >
                              Supprimer
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Popup>
                  )}
                </Polygon>
              );
            })}
        </FeatureGroup>

        {/* Marqueur de la ferme */}
        {farmLocation && (
          <Marker position={[farmLocation.latitude, farmLocation.longitude]}>
            <Popup>
              <div>
                <strong>{farmLocation.name}</strong>
                <br />
                <Typography variant="caption">
                  Position centrale de la ferme
                </Typography>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Marqueurs des capteurs */}
        {sensors.map((sensor, idx) => {
          if (!sensor.gps_latitude || !sensor.gps_longitude) {
            return null;
          }

          return (
            <Marker
              key={sensor.source_id || idx}
              position={[parseFloat(sensor.gps_latitude), parseFloat(sensor.gps_longitude)]}
              icon={sensorIcon}
              eventHandlers={{
                click: () => {
                  if (onSensorSelect && editMode === 'view') {
                    onSensorSelect(sensor);
                  }
                }
              }}
            >
              <Popup maxWidth={300}>
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {sensor.sensor_name || sensor.source_id}
                  </Typography>

                  {sensor.sensor_type && (
                    <Chip
                      label={sensor.sensor_type}
                      size="small"
                      sx={{ mb: 1, fontSize: '0.7rem' }}
                    />
                  )}

                  {sensor.location_description && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      📍 {sensor.location_description}
                    </Typography>
                  )}

                  <SensorMeasurementPopup latestMeasurement={sensor.latest_measurement} />

                  {/* Bouton Éditer */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleOpenEditSensor(sensor)}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Éditer
                  </Button>
                </Box>
              </Popup>
            </Marker>
          );
        })}

        {/* Polygones rendus dans FeatureGroup pour édition */}

        {/* Points GPS du périmètre en cours */}
        {isRecordingPerimeter && perimeterPoints.map((point, idx) => (
          <Marker
            key={`perimeter-${idx}`}
            position={[point.lat, point.lng]}
            icon={L.divIcon({
              className: 'perimeter-point',
              html: `<div style="width: 8px; height: 8px; background: red; border-radius: 50%; border: 2px solid white;"></div>`,
              iconSize: [8, 8]
            })}
          />
        ))}
      </MapContainer>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Dialog de création de capteur */}
      <AddSensorDialog
        open={showAddSensorDialog}
        onClose={() => setShowAddSensorDialog(false)}
        farmId={farmId}
        sectors={sectors}
        onSensorAdded={handleSensorAdded}
        initialLat={initialDialogCoords.lat}
        initialLng={initialDialogCoords.lng}
      />

      {/* Dialog de création de secteur */}
      <AddSectorDialog
        open={showAddSectorDialog}
        onClose={() => setShowAddSectorDialog(false)}
        farmId={farmId}
        compartments={farmDetail?.compartments || []}
        onSectorAdded={handleSectorAdded}
        initialLat={initialDialogCoords.lat}
        initialLng={initialDialogCoords.lng}
      />

      {/* Dialog de modification de capteur */}
      <EditSensorDialog
        open={showEditSensorDialog}
        onClose={() => {
          setShowEditSensorDialog(false);
          setSensorToEdit(null);
        }}
        farmId={farmId}
        sensor={sensorToEdit}
        sectors={sectors}
        onSensorUpdated={handleSensorUpdated}
      />

      {/* Dialog de modification de secteur */}
      <EditSectorDialog
        open={showEditSectorDialog}
        onClose={() => {
          setShowEditSectorDialog(false);
          setSectorToEdit(null);
        }}
        farmId={farmId}
        sector={sectorToEdit}
        compartments={farmDetail?.compartments || []}
        onSectorUpdated={handleSectorUpdated}
      />
    </Box>
  );
};

export default FarmMapView;
